import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const IdentityVerification = ({ onVerificationComplete, isRequired = false }) => {
  const { t } = useTranslation();
  const [verificationStep, setVerificationStep] = useState('initial');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Mock verification code for demo
  const mockVerificationCode = '123456';

  const handleSendCode = async () => {
    setIsLoading(true);
    setError('');
    setTimeout(() => {
      setVerificationStep('code-sent');
      setIsLoading(false);
    }, 1500);
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError('');
    setTimeout(() => {
      if (verificationCode === mockVerificationCode) {
        setVerificationStep('verified');
        onVerificationComplete?.(true);
      } else {
        setError(t('employeePortal.verification.invalidCode'));
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleSkip = () => {
    if (!isRequired) {
      onVerificationComplete?.(false);
    }
  };

  if (verificationStep === 'verified') {
    return (
      <div className="bg-success/10 border border-success/20 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" size={24} color="white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {t('employeePortal.verification.verifiedTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('employeePortal.verification.verifiedBody')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-warning/20 rounded-full flex items-center justify-center">
          <Icon name="Shield" size={24} className="text-warning" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('employeePortal.verification.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('employeePortal.verification.requiredHeading')}
          </p>
        </div>
      </div>

      {verificationStep === 'initial' && (
        <div>
          <div className="bg-muted/20 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-2">
                  {t('employeePortal.verification.whyTitle')}
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('employeePortal.verification.why1')}</li>
                  <li>• {t('employeePortal.verification.why2')}</li>
                  <li>• {t('employeePortal.verification.why3')}</li>
                  <li>• {t('employeePortal.verification.why4')}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 border border-border rounded-lg">
              <Icon name="Smartphone" size={20} className="text-primary" />
              <div className="flex-1">
                <h4 className="font-medium text-foreground">
                  {t('employeePortal.verification.smsTitle')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('employeePortal.verification.smsBody')}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:space-x-3 sm:gap-0">
              <Button
                onClick={handleSendCode}
                loading={isLoading}
                iconName="Send"
                iconPosition="left"
              >
                {t('employeePortal.verification.sendCode')}
              </Button>

              {!isRequired && (
                <Button variant="outline" onClick={handleSkip}>
                  {t('employeePortal.verification.skipForNow')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {verificationStep === 'code-sent' && (
        <div>
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <Icon name="MessageSquare" size={20} className="text-primary" />
              <div>
                <h4 className="font-medium text-foreground">
                  {t('employeePortal.verification.codeSentTitle')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('employeePortal.verification.codeSentBody')}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleVerifyCode} className="space-y-4">
            <Input
              label={t('employeePortal.verification.codeLabel')}
              type="text"
              placeholder={t('employeePortal.verification.codePlaceholder')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e?.target?.value)}
              error={error}
              maxLength={6}
              required
              description={t('employeePortal.verification.codeDemo')}
            />

            <div className="flex space-x-3">
              <Button
                type="submit"
                loading={isLoading}
                disabled={verificationCode?.length !== 6}
              >
                {t('employeePortal.verification.verifyCode')}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleSendCode}
                disabled={isLoading}
              >
                {t('employeePortal.verification.resendCode')}
              </Button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => setVerificationStep('initial')}
              className="text-sm text-primary hover:underline"
            >
              {t('employeePortal.verification.useDifferentMethod')}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start space-x-2">
          <Icon name="Lock" size={16} className="text-muted-foreground mt-0.5" />
          <div>
            <p className="text-sm text-muted-foreground">
              {t('employeePortal.verification.securityNotice')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdentityVerification;
